import {
  purchaseSeatsLanguages,
  purchaseGiftCardLanguages,
  redeemGiftCardLanguages,
} from "./helpers";
import { routes } from "@/global/routes";
import { constants, vendorUUIDs } from "../../constants";
import type { TeamConfig } from "@/global/hooks/useTeamConfig";
import { translations } from "./translations";
import {
  DefaultRegistrationTerms,
  DefaultCheckoutTerms,
} from "../../default_terms_and_conditions";

const LogoImage = "https://ss-leagues.s3.amazonaws.com/PDA/grandtheatre.png";
const BannerImage =
  "https://ss-leagues.s3.amazonaws.com/PDA/Hero-2000x2000_rgb.jpg";
const FaviconImage = "https://ss-leagues.s3.amazonaws.com/PDA/grandtheatre.png";

const config: TeamConfig = {
  teamSchema: "grandtheatre_giftcard",
  browserTitle: "Grand Théâtre Gift Cards",
  packages: false,
  defaultRoute: routes.giftCard,
  phoneNumber: "",
  teamName: "Grand Théâtre de Québec",

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: true,
    redirectGiftCardUrl: routes.giftCard,
    isCheckoutSaveCardEnabled: true,
    maxGiftPurchaseCountAllowed: 8,

    enableConfirmationModal: true,
    hideBannerOverlay: true,
  },

  // disabled routes
  disabledRoutes: [routes.purchaseSeats],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: false,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/grandtheatre/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    logoNameImage: "",
    findGamesBannerImage: "",
    seatMap3dPreviewImage: "",
    faviconImage: FaviconImage,
    authBackgroundImage: BannerImage,
    checkoutStatusPageImage: BannerImage,
    giftCardStandalonePageImage: BannerImage,
    // source is 4486x990 (~4.53:1); keep the same ratio or the header stretches it
    logoImage: LogoImage + "?width=245&height=54",
  },

  colors: {
    primary: "#42B3D9",
  },

  howItWorks: [],
  faqs: [],

  i18nConfig: {
    lng: constants.locales.fr,
    resources: translations,
  },

  uiConfigs: {
    fontFamily: "Arial, sans-serif",
    bannerImage: {
      title: "Grand Théâtre Gift Cards",
      isBackgroundBasedBanner: true,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: false,
    },
    hamburgerMenu: {
      purchaseHistoryEnabled: false,
    },
  },

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    purchaseGiftCard: purchaseGiftCardLanguages,
    redeemGiftCard: redeemGiftCardLanguages,

    giftCard: {
      title: "Grand Théâtre Gift Cards",
      createGiftCard: {
        description:
          "Share the magic of Grand Théâtre de Québec with a friend or loved one. Upon purchase of a gift card, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift card is not refundable.",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Grand Théâtre de Québec shows.",
          },
          sendNowTrue: {
            description:
              "The Recipient will be sent an email with the unique code for them to redeem their gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Grand Théâtre de Québec shows.",
          },
        },
      },

      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Grand Théâtre de Québec account above.",
        },
        logout: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Grand Théâtre de Québec account.",
        },
        success: {
          description:
            "has been added to your Grand Théâtre de Québec account!",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
